import React, { useState, useEffect } from "react";
import { useAuth } from "context/AuthContext";
import "./CommentSection.css";
import dayjs from "dayjs";

export default function CommentSection({ guest, bookingDate, chatMode = false, sseTrigger = 0 }) {
  const { user } = useAuth();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyingLoading, setReplyingLoading] = useState(false);
  const [replies, setReplies] = useState({});
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  const reservationId = guest.reservationId;
  const userName = user?.name || user?.username || "User";
  const userInitial = userName.charAt(0).toUpperCase();

  const TABLE_URL = process.env.REACT_APP_TEABLE_TABLE_URL_COMMENTS || "https://teable.namuve.com/api/table/tblqGKHWkCHhPq71rCo/record";
  const REPLY_TABLE_URL = process.env.REACT_APP_TEABLE_TABLE_URL_REPLIES || "https://teable.namuve.com/api/table/tbluupUT6D633eEiWDv/record";
  const NOTIFICATION_API = process.env.REACT_APP_TEABLE_TABLE_URL_NOTIFICATIONS || "https://teable.namuve.com/api/table/tblP2sVyihyhLjXYtRI/record";
  const TEABLE_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;

  const sendToGoogleChat = async (message, type = "comment") => {
    // Google Chat Webhook
    const GOOGLE_CHAT_WEBHOOK = process.env.REACT_APP_GOOGLE_CHAT_WEBHOOK_COMMENTS || "https://chat.googleapis.com/v1/spaces/AAQATia2_pc/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=5-ONneVmdY5TJo8dqggt-BtC2EnTdAFZAOET3ZywDew";

    const guestName = guest.guestName || "Guest";
    const aptName = guest.apartment || guest.listingName || "Unit";

    let actionText = "commented on";

    if (type.includes("edit")) {
      actionText = type.includes("reply") ? "edited reply on" : "edited comment on";
    } else if (type.includes("reply")) {
      actionText = "replied to";
    }

    const text = `👤 *${userName}* ${actionText} reservation of *${guestName} (${aptName} 🏠)*\n\n${message.trim()}\n\n🔗 https://dashboard.hostaway.com/reservations/${reservationId}`;

    // ✅ Send to Google Chat
    try {
      const res = await fetch(GOOGLE_CHAT_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("Google Chat error:", res.status, err);
      }
    } catch (err) {
      console.error("Network error:", err);
    }

    // ✅ Send to Mattermost
    const mattermostMessage = `${userName} commented on reservation of ${guestName} (${aptName} 🏠)

${message.trim()}

Reservation ID: [${reservationId}](https://dashboard.hostaway.com/reservations/${reservationId})`;

    console.log("🔵 Attempting to send Mattermost notification...");
    console.log("📝 Message:", mattermostMessage);
    console.log("📝 Channel ID:", process.env.REACT_APP_MATTERMOST_CHANNEL_ID_COMMENTS);

    try {
      const mmUrl = process.env.REACT_APP_MATTERMOST_WEBHOOK_URL || "https://chat.team.namuve.com/api/v4/posts";
      const mmToken = process.env.REACT_APP_MATTERMOST_TOKEN || "ymptq118c3be8jtyxpghf811xc";
      const mmChannel = process.env.REACT_APP_MATTERMOST_CHANNEL_ID_COMMENTS || "3wgp8gakejgci86w8nowyxnhia";

      const res = await fetch(mmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${mmToken}`
        },
        body: JSON.stringify({
          channel_id: mmChannel,
          message: mattermostMessage
        }),
      });

      console.log("📡 Mattermost response status:", res.status);

      if (!res.ok) {
        const err = await res.text();
        console.error("❌ Mattermost error:", res.status, err);
      } else {
        const data = await res.json();
        console.log("✅ Mattermost comment notification sent successfully!");
        console.log("📨 Response data:", data);
      }
    } catch (err) {
      console.error("❌ Mattermost network error:", err);
      console.error("❌ Error details:", err.message);
    }
  };

  /* ------------------------------------------------- POST NOTIFICATION ------------------------------------------------- */
  const handleSendNotification = async (commentText, type = "comment") => {
    if (!reservationId || !commentText?.trim()) return;

    const payload = {
      user: userName,
      text: commentText.trim(),
      reservation_id: reservationId,
      type: type,
      apt: guest.apartment || guest.listingName || "N/A",
      guest_name: guest.guestName || "Unknown",
    };

    console.log("Sending notification → /api/fdo/notifications");
    console.log("Type →", type);

    try {
      const res = await fetch("/api/fdo/notifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("Notification failed:", res.status, err);
      } else {
        console.log("Notification saved successfully to local DB:", type);
      }
    } catch (err) {
      console.error("Network error:", err);
    }
  };

  /* ------------------------------------------------- FETCH COMMENTS ------------------------------------------------- */
  useEffect(() => {
    fetchComments();
  }, [reservationId, sseTrigger]);

  const fetchComments = async () => {
    if (!reservationId) return;
    setFetching(true);
    try {
      const res = await fetch(`/api/fdo/comments?reservationId=${reservationId}&_=${Date.now()}`, { cache: "no-store" });
      const data = await res.json();
      if (data.success && data.records) {
        const mapped = data.records.map((r) => ({
            id: r.id,
            teableId: r.id,
            commentId: r.id,
            text: r.comment || "",
            author: r.user || "Unknown",
            timestamp: r.createdAt,
            avatar: (r.user || "U").charAt(0).toUpperCase(),
        }));

        setComments(mapped);
        setReplies({});
      }
    } catch (err) {
      console.error("Fetch comments error:", err);
    } finally {
      setFetching(false);
    }
  };

  /* ------------------------------------------------- FETCH REPLIES FOR ALL COMMENTS ON LOAD ------------------------------------------------- */
  useEffect(() => {
    if (comments.length === 0) return;

    comments.forEach((c) => {
      // If we don't have replies OR sseTrigger fired (forcing a refresh)
      fetchReplies(c.commentId);
    });
  }, [comments, sseTrigger]);

  /* ------------------------------------------------- POST COMMENT ------------------------------------------------- */
  const handleSend = async () => {
    if (!newComment.trim() || !reservationId) return;
    setLoading(true);

    const commentText = newComment.trim();

    try {
      const payload = {
        reservation_id: reservationId,
        comment: newComment,
        user: userName,
      };
      const res = await fetch("/api/fdo/comments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setNewComment("");
        fetchComments();
        handleSendNotification(commentText, "commented");
        sendToGoogleChat(commentText, "comment");
      } else {
        const err = await res.json();
        alert("Save failed: " + (err.message || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------- EDIT ------------------------------------------------- */
  const handleEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.text);
  };
  const handleUpdate = async (teableId) => {
    if (!editText.trim()) return;

    try {
      const res = await fetch(`/api/fdo/comments/${teableId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ comment: editText }),
      });

      if (!res.ok) throw new Error((await res.json()).message);

      // EDIT SUCCESS → SEND NOTIFICATION
      handleSendNotification(
        editText,
        "edited comment" // Dynamic type
      );

      fetchComments();
      setEditingId(null);
      setEditText("");
      sendToGoogleChat(editText, "edited comment");

    } catch (err) {
      alert("Update failed: " + err.message);
    }
  };

  const handleEditReply = (r) => {
    setEditingReplyId(r.id);
    setEditReplyText(r.text);
  };

  const handleUpdateReply = async (teableId) => {
    if (!editReplyText.trim()) return;
    try {
      const res = await fetch(`/api/fdo/replies/${teableId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reply: editReplyText }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      handleSendNotification(
        editReplyText,
        "edited reply"
      );

      // Refresh replies
      const commentId = Object.keys(replies).find(key => replies[key].some(rep => rep.id === teableId));
      if (commentId) fetchReplies(commentId);

      setEditingReplyId(null);
      setEditReplyText("");
      sendToGoogleChat(editReplyText, "edited reply");
    } catch (err) {
      alert("Update reply failed: " + err.message);
    }
  };

  /* ------------------------------------------------- DELETE ------------------------------------------------- */
  /*const handleDelete = async (teableId) => {
    try {
      await fetch(`${TABLE_URL}/${teableId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${TEABLE_TOKEN}` },
      });
      fetchComments();
    } catch {
      alert("Delete failed");
    }
  };*/

  /* ------------------------------------------------- REPLY: START ------------------------------------------------- */
  const startReply = (c) => {
    setReplyingTo(c.id);
    setReplyText("");
    fetchReplies(c.commentId);
  };

  /* ------------------------------------------------- FETCH REPLIES (USING SEARCH API) ------------------------------------------------- */
  const fetchReplies = async (commentId) => {
    if (!commentId) return;

    try {
      const res = await fetch(`/api/fdo/replies?commentId=${commentId}&_=${Date.now()}`, { cache: "no-store" });
      const data = await res.json();

      if (data.success && data.records) {
        const formatted = data.records.map((r) => ({
          id: r.id,
          teableId: r.id,
          text: r.reply || "",
          author: r.user || userName,
          time: r.createdAt,
          avatar: (r.user || userName).charAt(0).toUpperCase(),
        }));

        setReplies((prev) => ({ ...prev, [commentId]: formatted }));
      }
    } catch (err) {
      console.error("Fetch replies error:", err);
    }
  };

  /* ------------------------------------------------- POST REPLY ------------------------------------------------- */
  const handleReply = async (c) => {
    if (!replyText.trim()) return;
    setReplyingLoading(true);
    try {
      const payload = {
        comment_id: c.commentId,
        reply: replyText,
        user: userName,
      };
      const res = await fetch("/api/fdo/replies", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {

        // SEND NOTIFICATION: Reply added
        handleSendNotification(
          replyText,
          "replyed"
        );
        setReplyText("");
        setReplyingTo(null);
        fetchReplies(c.commentId);
        sendToGoogleChat(replyText, "reply");
      } else {
        const err = await res.json();
        alert("Reply failed: " + (err.message || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setReplyingLoading(false);
    }
  };

  /* ------------------------------------------------- ICON ------------------------------------------------- */
  const ReplyIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
      width="16"
      height="16"
      fill="currentColor"
      style={{ marginRight: "5px" }}
    >
      <path d="M268.2 82.4C280.2 87.4 288 99 288 112L288 192L400 192C497.2 192 576 270.8 576 368C576 481.3 494.5 531.9 475.8 542.1C473.3 543.5 470.5 544 467.7 544C456.8 544 448 535.1 448 524.3C448 516.8 452.3 509.9 457.8 504.8C467.2 496 480 478.4 480 448.1C480 395.1 437 352.1 384 352.1L288 352.1L288 432.1C288 445 280.2 456.7 268.2 461.7C256.2 466.7 242.5 463.9 233.3 454.8L73.3 294.8C60.8 282.3 60.8 262 73.3 249.5L233.3 89.5C242.5 80.3 256.2 77.6 268.2 82.6z" />
    </svg>
  );

  const formatTime = (dateString) => {
    const date = new Date(dateString);

    // Format: "Nov 25, 2025 at 3:42 PM"
    return dayjs(date).format("MMM D, YYYY [at] h:mm A");
  };

  /* ------------------------------------------------- RENDER ------------------------------------------------- */
  const commentsList = (
    <>
      {fetching ? (
        <p className="text-center text-muted">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="text-center text-muted">No comments yet.</p>
      ) : (
        comments.map((c) => (
          <div key={c.id} className="media mb-4">
            {chatMode ? (
              // ------------------------- CHAT MODE LAYOUT -------------------------
              <div className="d-flex w-100">
                {/* Avatar Left */}
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold flex-shrink-0"
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    fontSize: "0.9rem",
                    marginRight: "12px",
                  }}
                >
                  {c.avatar}
                </div>

                {/* Content Right */}
                <div className="flex-grow-1">
                  {/* Header Row: Name (Left) | Time + Reply + Edit (Right) */}
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div>
                      <strong style={{ fontSize: "0.95rem" }}>{c.author}</strong>
                    </div>
                    {/* Actions & Time */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small mr-2">
                        {dayjs(c.timestamp).format("MMM D, YYYY [at] h:mm A")}
                      </span>

                      {!editingId && (
                        <a
                          href="#"
                          className="reply-link text-muted small"
                          onClick={(e) => {
                            e.preventDefault();
                            startReply(c);
                          }}
                          style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                        >
                          <ReplyIcon /> Reply
                        </a>
                      )}
                      {c.author === userName && !editingId && (
                        <a
                          href="#"
                          className="text-muted small ml-2"
                          onClick={(e) => {
                            e.preventDefault();
                            handleEdit(c);
                          }}
                        >
                          Edit
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Message Bubble */}
                  {editingId === c.id ? (
                    <div className="mt-2">
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        autoFocus
                        style={{ fontSize: "0.9rem" }}
                      />
                      <div className="d-flex gap-2 mt-2">
                        <button className="btn btn-sm btn-primary" onClick={() => handleUpdate(c.teableId)} disabled={!editText.trim()} style={{ backgroundColor: "#5e6b47", borderColor: "#5e6b47" }}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => { setEditingId(null); setEditText(""); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="p-3"
                      style={{
                        backgroundColor: "#f9fafb",
                        borderRadius: "12px",
                        fontSize: "0.95rem",
                        color: "#374151",
                        borderTopLeftRadius: "0px"
                      }}
                    >
                      {c.text}
                    </div>
                  )}

                  {/* REPLIES (Chat Mode) */}
                  {(replies[c.commentId] || []).map((r) => (
                    <div key={r.id} className="d-flex mt-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold flex-shrink-0"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#f3f4f6",
                          color: "#374151",
                          fontSize: "0.8rem",
                          marginRight: "10px",
                        }}
                      >
                        {r.avatar}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div>
                            <strong className="mr-2" style={{ fontSize: "0.85rem" }}>{r.author}</strong>
                            <span className="text-muted small" style={{ fontSize: "0.75rem" }}>
                              {dayjs(r.time).format("MMM D, YYYY [at] h:mm A")}
                            </span>
                          </div>
                          {r.author === userName && !editingReplyId && (
                            <a
                              href="#"
                              className="text-muted small"
                              onClick={(e) => { e.preventDefault(); handleEditReply(r); }}
                            >
                              Edit
                            </a>
                          )}
                        </div>

                        {editingReplyId === r.id ? (
                          <div className="mt-1">
                            <textarea
                              className="form-control form-control-sm"
                              rows="1"
                              value={editReplyText}
                              onChange={(e) => setEditReplyText(e.target.value)}
                              autoFocus
                            />
                            <div className="d-flex gap-2 mt-1">
                              <button className="btn btn-sm btn-primary" onClick={() => handleUpdateReply(r.teableId)} style={{ backgroundColor: "#5e6b47", borderColor: "#5e6b47" }}>Save</button>
                              <button className="btn btn-sm btn-secondary" onClick={() => { setEditingReplyId(null); setEditReplyText(""); }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="p-2 px-3"
                            style={{
                              backgroundColor: "#f9fafb",
                              borderRadius: "10px",
                              fontSize: "0.9rem",
                              color: "#374151",
                              borderTopLeftRadius: "0px"
                            }}
                          >
                            {r.text}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* REPLY INPUT (Chat Mode) */}
                  {replyingTo === c.id && (
                    <div className="mt-3">
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        placeholder="Write a reply..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        autoFocus
                        style={{ borderRadius: "8px" }}
                      />
                      <div className="d-flex gap-2 mt-2">
                        <button className="btn btn-sm btn-primary" onClick={() => handleReply(c)} disabled={!replyText.trim() || replyingLoading} style={{ backgroundColor: "#5e6b47", borderColor: "#5e6b47" }}>
                          {replyingLoading ? "..." : "Post"}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => { setReplyingTo(null); setReplyText(""); }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // ------------------------- CLASSIC MODE LAYOUT -------------------------
              <div className="media-body">
                {/* HEADER */}
                <div className="comment-header d-flex justify-content-between align-items-center w-100">
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold"
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        fontSize: "1rem",
                      }}
                    >
                      {c.avatar}
                    </div>
                    <div className="ml-3">
                      <div>
                        <strong className="d-inline">{c.author}</strong>
                        <span className="text-muted small ml-2">
                          - {formatTime(c.timestamp)}
                        </span>
                      </div>

                      {editingId === c.id ? (
                        <textarea
                          className="form-control form-control-sm mt-2"
                          rows="2"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          autoFocus
                          style={{ fontSize: "0.875rem" }}
                        />
                      ) : (
                        <p className="mt-1 mb-0">{c.text}</p>
                      )}
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="d-flex gap-2">
                    {editingId === c.id ? (
                      <>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUpdate(c.teableId)}
                          disabled={!editText.trim()}
                          style={{ backgroundColor: "#5e6b47", borderColor: "#5e6b47" }}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setEditingId(null);
                            setEditText("");
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <a
                          href="#"
                          className="reply-link"
                          onClick={(e) => {
                            e.preventDefault();
                            startReply(c);
                          }}
                        >
                          <ReplyIcon /> reply
                        </a>

                        {c.author === userName && (
                          <>
                            <a
                              href="#"
                              className="text-primary small"
                              onClick={(e) => {
                                e.preventDefault();
                                handleEdit(c);
                              }}
                            >
                              edit
                            </a>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* REPLIES LIST */}
                {(replies[c.commentId] || []).map((r) => (
                  <div key={r.id} className="media mt-4">
                    <div className="media-body">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        {/* LEFT: Avatar + Name + Time + Text */}
                        <div className="d-flex align-items-center flex-grow-1">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#f3f4f6",
                              color: "#374151",
                              fontSize: "0.875rem",
                            }}
                          >
                            {r.avatar}
                          </div>
                          <div className="ml-3 flex-grow-1">
                            <div>
                              <strong className="d-inline">{r.author}</strong>
                              <span className="text-muted small ml-2">
                                - {formatTime(r.time)}
                              </span>
                            </div>

                            {/* EDIT MODE */}
                            {editingReplyId === r.id ? (
                              <textarea
                                className="form-control form-control-sm mt-1"
                                rows="1"
                                value={editReplyText}
                                onChange={(e) => setEditReplyText(e.target.value)}
                                autoFocus
                                style={{ fontSize: "0.8rem" }}
                              />
                            ) : (
                              <p className="mt-1 mb-0" style={{ fontSize: "0.875rem" }}>
                                {r.text}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* RIGHT: Edit button (only if author matches) */}
                        {r.author === userName && (
                          <div className="d-flex gap-1">
                            {editingReplyId === r.id ? (
                              <>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleUpdateReply(r.teableId)}
                                  disabled={!editReplyText.trim()}
                                  style={{ backgroundColor: "#5e6b47", borderColor: "#5e6b47" }}
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => {
                                    setEditingReplyId(null);
                                    setEditReplyText("");
                                  }}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <a
                                href="#"
                                className="text-primary small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditReply(r);
                                }}
                              >
                                edit
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* REPLY INPUT */}
                {replyingTo === c.id && (
                  <div className="media mt-4">
                    <div className="media-body">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                            fontSize: "0.875rem",
                            marginRight: "12px",
                            flexShrink: 0,
                          }}
                        >
                          {userInitial}
                        </div>

                        <div className="flex-grow-1">
                          <textarea
                            className="form-control form-control-sm"
                            rows="2"
                            placeholder="Write a reply..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            autoFocus
                            style={{
                              resize: "none",
                              borderRadius: "8px",
                              fontSize: "0.875rem",
                            }}
                          />
                          <div className="d-flex gap-2 mt-2">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleReply(c)}
                              disabled={!replyText.trim() || replyingLoading}
                              style={{ backgroundColor: "#5e6b47", borderColor: "#5e6b47" }}
                            >
                              {replyingLoading ? "..." : "Post"}
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))
      )}
    </>
  );

  if (chatMode) {
    return (
      <div className="d-flex flex-column h-100">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
          <h6 className="m-0 font-weight-bold">
            Comments <span className="ml-2 px-2 py-0 rounded" style={{ backgroundColor: "#e5e7eb", color: "#374151", fontSize: "0.85rem" }}>{comments.length}</span>
          </h6>
        </div>

        {/* List */}
        <div className="flex-grow-1 overflow-auto pr-1" style={{ maxHeight: "600px" }}>
          {commentsList}
        </div>

        {/* Input */}
        <div className="mt-3 pt-2 border-top">
          <div
            className="rounded-pill d-flex align-items-center px-1 py-1"
            style={{
              border: isInputFocused ? "2px solid #5e6b47" : "1px solid #ccc",
              transition: "border 0.2s ease-in-out"
            }}
          >
            <textarea
              className="form-control border-0"
              rows="1"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading}
              style={{
                resize: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
                fontSize: "0.95rem"
              }}
            />
            <button
              className="btn btn-link p-0 flex-shrink-0 d-flex align-items-center justify-content-center ml-2"
              style={{
                width: "36px",
                height: "36px",
                backgroundColor: "#5e6b47", // Requested green
                borderRadius: "10px",
                color: "white",
                marginRight: "4px"
              }}
              onClick={handleSend}
              disabled={!newComment.trim() || loading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                viewBox="0 0 16 16"
                style={{ transform: "rotate(45deg)", marginLeft: "-2px", marginBottom: "3px" }}
              >
                <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* CLASSIC VIEW */
  return (
    <div className="comment-section-wrapper">
      <div className="card">
        <div className="row">
          <div className="col-md-12">
            {/* DATES */}
            <div className="d-flex justify-content-between align-items-center mb-4 px-3">
              <small className="text-muted">
                <strong>Reservation Date:</strong>{" "}
                {guest.arrivalDate && guest.departureDate
                  ? `${dayjs(guest.arrivalDate).format("D MMM")} – ${dayjs(
                    guest.departureDate
                  ).format("D MMM, YYYY")}`
                  : "N/A"}
              </small>
              <small className="text-muted">
                <strong>Booking Date:</strong>{" "}
                {bookingDate
                  ? dayjs(bookingDate).format("D MMM, YYYY")
                  : "Not provided"}
              </small>
            </div>

            <h3 className="text-center mb-5">
              Comment section of {guest.guestName} - {guest.listingName}
            </h3>

            {/* INPUT */}
            <div className="d-flex align-items-start mb-4">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center font-weight-bold"
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  fontSize: "0.875rem",
                  marginRight: "12px",
                }}
              >
                {userInitial}
              </div>
              <div className="flex-grow-1">
                <div className="mb-1">
                  <strong style={{ fontSize: "0.9rem", color: "#1f2937" }}>
                    {userName}
                  </strong>
                </div>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={loading}
                  style={{
                    resize: "none",
                    borderRadius: "8px",
                    fontSize: "0.9rem",
                  }}
                />
                <div className="d-flex justify-content-end mt-2">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSend}
                    disabled={!newComment.trim() || loading}
                    style={{
                      borderRadius: "8px",
                      fontSize: "0.85rem",
                      padding: "6px 16px",
                    }}
                  >
                    {loading ? "..." : "Post"}
                  </button>
                </div>
              </div>
            </div>

            {/* COMMENTS LIST */}
            {commentsList}
          </div>
        </div>
      </div>
    </div>
  );
}